import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller } from 'react-hook-form';
import { Input } from 'components/layout';
import { useTheme } from '@mui/material/styles';
import { Box, Typography, Grid, Button } from '@mui/material';
import TemplatePollingModal from './TemplatePollingModal';
import { TableCustom } from 'components/common';
import SpeakerNotesOffIcon from '@mui/icons-material/SpeakerNotesOff';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';


const TemplatePolling = ({
  methods,
  policies,
  fieldData,
  fieldDataConst,
  isLoaded,
  showActions= true
}) => {
  const [t] = useTranslation();
  const [openModal, setOpenModal] = useState(false);
  const [pollingSelected, setPollingSelected] = useState();
  const [pollingState, setPollingState] = useState({
    isEdit: false,
    data: fieldDataConst.pollingType[0].value,
    options: fieldDataConst.pollingType
  });
  const theme = useTheme();
  const {
    getValues,
    setValue,
    control,
    handleSubmit
  } = methods;

  const onSubmit = (data) => {
    const pollingType = data[policies.pollingType.name];

    let polling = {
      [policies.pollingVariable.name]: data[policies.pollingVariable.name],
      [policies.pollingDescription.name]: data[policies.pollingDescription.name],
      [policies.pollingType.name]: data[policies.pollingType.name]
    };

    if (pollingType === fieldDataConst.pollingType[1].value) {
      polling = {
        ...polling,
        [policies.pollingRange.name]: [
          Number(data[policies.pollingNumberFrom.name]),
          Number(data[policies.pollingNumberTo.name])
        ],
        [policies.pollingNumberFrom.name]: data[policies.pollingNumberFrom.name],
        [policies.pollingNumberTo.name]: data[policies.pollingNumberTo.name]
      };
    }

    if (pollingType === fieldDataConst.pollingType[2].value) {
      polling = {
        ...polling,
        [policies.pollingOptionsValues.name]: data[policies.pollingOptionsValues.name]
      };
    }

    setValue(policies.pollings.name, [ ...getValues(policies.pollings.name), polling ]);
    handleCloseModal();
  };

  const onEditSubmit = (polling, index) => {
    let currentPollings = getValues(policies.pollings.name);
    currentPollings[index] = polling;
    setValue(policies.pollings.name, currentPollings);
    handleCloseModal();
  };

  const handleCloseModal = () => {
    setPollingSelected(null);
    setOpenModal(false);
  };

  const handleAdd = (data) => {
    const { pollingType } = data;
    if (!pollingType) setPollingState({ pollingType: fieldDataConst.pollingType[0].value });
    else setPollingState({ ...pollingState, data: { pollingType }, isEdit: false });
    setOpenModal(true);
  };

  const handleDelete = (polling) => {
    let currentPollings = getValues(policies.pollings.name);
    const newPollings = currentPollings.filter(p => p.pollingVariable !== polling.pollingVariable);
    setValue(policies.pollings.name, newPollings);
  };

  const handleEdit = (polling, index) => {
    const { pollingType } = polling;
    setPollingSelected({ ...polling, index });
    setPollingState({ ...pollingState, data: { pollingType }, isEdit: true });
    setOpenModal(true);
  };

  const columns = [
    { label: t('templateList.form.pollingVariable.label'), key: 'pollingVariable' },
    { label: t('templateList.form.pollingType.label'), key: 'pollingType' },
    { label: t('templateList.form.pollingDescription.label'), key: 'pollingDescription' },
  ];

  let actions = [
    {
      label: t('templateList.stepper.polling.table.actionEdit'),
      icon: <EditOutlinedIcon color='primary'/>,
      handle: handleEdit
    },
    {
      label: t('templateList.stepper.polling.table.actionDelete'),
      icon: <HighlightOffIcon color='primary'/>,
      handle: handleDelete
    }
  ];

  if (!showActions) {
    actions = [];
  }

  return (
    <>
      <Box sx={{ overflow: 'hidden' }}>
        { showActions &&
          <Typography variant="h6" sx={{ padding: '8px 0px' }}>
            {t('templateList.stepper.polling.title')}
          </Typography>
        }
        <Grid container spacing={2} alignItems="flex-start">
          { showActions &&
          <Grid item xs={12} sm={4} md={4} lg={4}>
            <Input
              { ...policies.pollingType }
              control={control}
              options={fieldDataConst.pollingType}
              sx={{ width: '100%' }}
            />
          </Grid>
          }
          { showActions &&
            <Grid item xs={12} sm={4} md={4} lg={4} sx={{
                display: 'flex',
                alignItems: 'flex-end',
                height: '84px',
              }}
            >
              <Button variant="outlined" onClick={handleSubmit(handleAdd)}>
                {t('templateList.stepper.polling.buttonLabel')}
              </Button>
            </Grid>
          }
          <Grid item xs={12} sm={12} md={12} lg={12}>
            <Box
              sx={{
                border: `1px solid ${theme.palette.grey[400]}`,
                borderRadius: 1,
                overflow: 'hidden',
              }}
            >
              <Controller
                control={control}
                name={policies.pollings.name}
                id={policies.pollings.id}
                render={({ field: { value } }) => (
                  <TableCustom
                    columns={columns}
                    content={value}
                    actions={actions}
                    config={{
                      notResults: {
                        icon: SpeakerNotesOffIcon,
                        title:
                          'templateList.stepper.polling.table.title',
                        description:
                          'templateList.stepper.polling.table.description',
                      }
                    }}
                  />
                )}
              />
            </Box>
          </Grid>
        </Grid>
      </Box>
      { openModal &&
        <TemplatePollingModal
          open={openModal}
          polling={pollingState}
          currentPollings={getValues(policies.pollings.name)}
          handleClose={handleCloseModal}
          defaultValues={pollingSelected}
          onSubmit={onSubmit}
          onEditSubmit={onEditSubmit}
        />
      }
    </>
  );
};

export default TemplatePolling;
